/**
 * Mock workouts service.
 * Mirrors the real workouts.service.js API with in-memory data.
 */
import { MOCK_WORKOUTS, MOCK_TEMPLATES, MOCK_WORKOUT_STATS } from '../../data/mock/mock-workouts.data';
import { mockDelay, mockLog, mockId } from './mock-delay';

// In-memory state
let workouts = [...MOCK_WORKOUTS];
let templates = [...MOCK_TEMPLATES];

const paginate = (items, lastDoc, pageLimit) => {
  let startIndex = 0;
  if (lastDoc) {
    const idx = items.findIndex((w) => w.id === lastDoc);
    if (idx !== -1) startIndex = idx + 1;
  }
  const page = items.slice(startIndex, startIndex + pageLimit);
  const newLastDoc = page.length > 0 ? page[page.length - 1].id : null;
  const hasMore = startIndex + pageLimit < items.length;
  return { page, lastDoc: newLastDoc, hasMore };
};

export const createWorkout = async (workoutData) => {
  mockLog('WorkoutsService', 'createWorkout', workoutData.title);
  await mockDelay();
  const workout = {
    id: `mock_wkt_${mockId()}`,
    userId: workoutData.userId,
    title: workoutData.title || 'Workout',
    workoutType: workoutData.workoutType || 'other',
    exercises: workoutData.exercises || [],
    duration: workoutData.duration || 0,
    caloriesBurned: workoutData.caloriesBurned || 0,
    notes: workoutData.notes || '',
    sharedAsPostId: workoutData.sharedAsPostId || null,
    templateId: workoutData.templateId || null,
    isTemplate: false,
    completedAt: new Date().toISOString(),
    createdAt: new Date().toISOString(),
    _docSnapshot: null,
  };
  workouts = [workout, ...workouts];
  return workout;
};

export const getWorkouts = async (userId, lastDoc = null, pageLimit = 20) => {
  mockLog('WorkoutsService', 'getWorkouts', { userId, lastDoc, pageLimit });
  await mockDelay();
  const userWorkouts = workouts
    .filter((w) => w.userId === userId && !w.isTemplate)
    .sort((a, b) => new Date(b.completedAt) - new Date(a.completedAt));
  const { page, lastDoc: newLast, hasMore } = paginate(userWorkouts, lastDoc, pageLimit);
  return { workouts: page, lastDoc: newLast, hasMore };
};

export const getWorkoutById = async (workoutId) => {
  mockLog('WorkoutsService', 'getWorkoutById', workoutId);
  await mockDelay();
  return workouts.find((w) => w.id === workoutId) || templates.find((t) => t.id === workoutId) || null;
};

export const deleteWorkout = async (workoutId) => {
  mockLog('WorkoutsService', 'deleteWorkout', workoutId);
  await mockDelay();
  workouts = workouts.filter((w) => w.id !== workoutId);
  return workoutId;
};

export const updateWorkoutSharedPost = async (workoutId, postId) => {
  mockLog('WorkoutsService', 'updateWorkoutSharedPost', { workoutId, postId });
  await mockDelay();
  const idx = workouts.findIndex((w) => w.id === workoutId);
  if (idx !== -1) workouts[idx] = { ...workouts[idx], sharedAsPostId: postId };
};

export const getWorkoutStats = async (userId) => {
  mockLog('WorkoutsService', 'getWorkoutStats', userId);
  await mockDelay();
  return { ...MOCK_WORKOUT_STATS };
};

export const getWorkoutsByDateRange = async (userId, startDate, endDate) => {
  mockLog('WorkoutsService', 'getWorkoutsByDateRange', { startDate, endDate });
  await mockDelay();
  const start = new Date(startDate);
  const end = new Date(endDate);
  return workouts
    .filter((w) => {
      if (w.userId !== userId || w.isTemplate) return false;
      const d = new Date(w.completedAt);
      return d >= start && d <= end;
    })
    .sort((a, b) => new Date(b.completedAt) - new Date(a.completedAt));
};

export const saveTemplate = async (workoutData) => {
  mockLog('WorkoutsService', 'saveTemplate', workoutData.title);
  await mockDelay();
  const template = {
    id: `mock_tpl_${mockId()}`,
    userId: workoutData.userId,
    title: workoutData.title || 'My Template',
    workoutType: workoutData.workoutType || 'strength',
    exercises: (workoutData.exercises || []).map((ex) => ({
      name: ex.name,
      sets: ex.sets.map((s) => ({ reps: s.reps || 0, weight: s.weight || 0, unit: s.unit || 'lbs', completed: false })),
      notes: ex.notes || '',
      restTime: ex.restTime || 60,
    })),
    duration: 0,
    caloriesBurned: 0,
    notes: workoutData.notes || '',
    sharedAsPostId: null,
    templateId: null,
    isTemplate: true,
    completedAt: new Date().toISOString(),
    createdAt: new Date().toISOString(),
    _docSnapshot: null,
  };
  templates = [template, ...templates];
  return template;
};

export const getTemplates = async (userId) => {
  mockLog('WorkoutsService', 'getTemplates', userId);
  await mockDelay();
  return templates.filter((t) => t.userId === userId).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
};

export const deleteTemplate = async (templateId) => {
  mockLog('WorkoutsService', 'deleteTemplate', templateId);
  await mockDelay();
  templates = templates.filter((t) => t.id !== templateId);
  return templateId;
};

export const getPopularTemplates = async (pageLimit = 10) => {
  mockLog('WorkoutsService', 'getPopularTemplates');
  await mockDelay();
  return templates.slice(0, pageLimit);
};
